import { motion } from 'framer-motion'
import { Code2, Trophy, Users, Globe, Shield, Laptop, Settings, HeartHandshake } from 'lucide-react'
import pic from '../../assets/pic.jpg'

const About = () => {
  const stats = [
    { icon: Code2, value: '5+', label: 'Years Experience' },
    { icon: Trophy, value: '50+', label: 'Projects Completed' },
    { icon: Users, value: '10+', label: 'Open Source Contributions' },
    { icon: Globe, value: '3+', label: 'Countries Worked With' }
  ]

  const achievements = [
    {
      title: 'Keycloak Contributor',
      description: 'Active contributor to Keycloak open-source IAM with merged PRs in version 26.1.0',
      icon: Shield
    },
    {
      title: 'Full Stack Expertise',
      description: 'End-to-end experience building production systems with React, Spring Boot, and cloud infrastructure',
      icon: Laptop
    },
    {
      title: 'DevOps Practitioner',
      description: 'CI/CD pipelines, containerization, Kubernetes, and cloud-native deployments',
      icon: Settings
    },
    {
      title: 'Open Source Advocate',
      description: 'Passionate about community-driven development and collaborative engineering',
      icon: HeartHandshake
    }
  ]

  return (
    <section className="section section-border">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-16 max-w-2xl"
        >
          <span className="section-label mb-4">About</span>
          <h2 className="text-balance mb-6">Engineer, contributor, and lifelong learner.</h2>
          <p className="text-lg text-zinc-500 dark:text-zinc-400">
            I care about building software that lasts: clean architecture, observable systems, and teams that move fast without breaking things.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="space-y-5 text-zinc-500 dark:text-zinc-400 lg:col-span-5"
          >
            <p className="leading-relaxed">
              I&apos;m a Full Stack Software Engineer based in Cameroon, with over 5 years of experience building robust, scalable applications. My work spans enterprise Java backends, React frontends, and the DevOps practices that keep them running in production.
            </p>
            <p className="leading-relaxed">
              Currently at Adorsys, I specialize in Spring Boot microservices, identity management with Keycloak, and cloud-native deployments. I&apos;m particularly proud of my contributions to the Keycloak open-source project.
            </p>
            <p className="leading-relaxed">
              Outside of shipping code, I learn new technologies, write about engineering practices, and contribute to open source. I believe the best software comes from curiosity, discipline, and collaboration.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
              viewport={{ once: true }}
              className="card card-hover row-span-2 flex flex-col justify-between"
            >
              <div>
                <div className="mb-4 aspect-[4/3] overflow-hidden rounded-lg border border-zinc-100 dark:border-zinc-800">
                  <img
                    src={pic}
                    alt="Bertrand Ogen"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                  Based in
                </div>
                <div className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                  Cameroon
                </div>
              </div>
            </motion.div>

            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.05, ease: [0, 0, 0.2, 1] }}
                viewport={{ once: true }}
                className="card card-hover"
              >
                <stat.icon className="mb-4 h-5 w-5 text-cyan-500" strokeWidth={1.5} />
                <div className="font-mono text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  {stat.value}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{stat.label}</div>
              </motion.div>
            ))}

            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: (index + 4) * 0.05, ease: [0, 0, 0.2, 1] }}
                viewport={{ once: true }}
                className="card card-hover"
              >
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900">
                  <achievement.icon className="h-4 w-4 text-cyan-500" strokeWidth={1.5} />
                </div>
                <h4 className="mb-1 text-sm font-semibold text-zinc-950 dark:text-zinc-50">
                  {achievement.title}
                </h4>
                <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                  {achievement.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.blockquote
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mt-16 max-w-3xl border-l-2 border-cyan-500 pl-6"
        >
          <p className="text-lg italic leading-relaxed text-zinc-700 dark:text-zinc-300">
            &ldquo;Great software is not just about writing code — it is about solving real problems, building sustainable solutions, and continuously learning.&rdquo;
          </p>
          <footer className="mt-3 font-mono text-xs text-zinc-400">— Bertrand Ogen</footer>
        </motion.blockquote>
      </div>
    </section>
  )
}

export default About
